import {autoUpdate, flip, offset, shift, useFloating} from '@floating-ui/react';
import {CountryCode} from 'libphonenumber-js';
import {Portal, PortalManager, Flag, DataStatus} from 'platform/components';
import {formatPhoneNumber} from 'platform/locale';
import {useTheme} from 'styled-components';
import {Nullish} from 'utility-types';

import {useEffect} from 'react';

import {isNotNil} from 'ramda';

import {useGetTagsForEntityQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {TestDriveTooltipCard} from '@dms/features/test-drive';
import {getNaturalPersonFullName} from '@dms/shared';

import {parseDate} from 'shared/web';

import {TestDriveCalendarEvent} from './utils/transformTestDriveToCalendarEvent';

interface CustomerDetailsPopupProps {
  event: TestDriveCalendarEvent;
  isVisible: boolean;
  anchorElement: HTMLElement | null;
}

const OFFSET_FROM_CURSOR = 10;

export function CustomerDetailsPopup(props: CustomerDetailsPopupProps) {
  const theme = useTheme();

  const customerId = props.event.customerId;

  const {refs, floatingStyles} = useFloating({
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [
      offset(OFFSET_FROM_CURSOR),
      flip({
        fallbackPlacements: ['right-end', 'left-start', 'left-end', 'top', 'bottom'],
        padding: 8,
      }),
      shift({padding: 8, limiter: undefined}),
    ],
    whileElementsMounted: autoUpdate,
  });
  const {setFloating} = refs;

  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {customerId},
    {
      skip: !customerId || !props.isVisible,
    }
  );

  const {
    data: vehicleData,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId: props.event.vehicleId},
    {
      skip: !props.event.vehicleId || !props.isVisible,
    }
  );

  const {data: entityTags, isLoading: isEntityTagsLoading} = useGetTagsForEntityQuery({
    recordId: customerId,
    resourceId: EntityResourceIds.customer,
  });

  useEffect(
    function syncAnchorReference() {
      refs.setReference(props.anchorElement);
    },
    [props.anchorElement, refs]
  );

  if (!props.isVisible || !customer) {
    return null;
  }

  const testDriveEvent = props.event;
  const isCompany = customer?.discriminator === 'BUSINESS';

  const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];
  const customerName =
    customer?.discriminator === 'NATURAL_PERSON'
      ? getNaturalPersonFullName(customerInfo)
      : customer.businessInfo?.businessInfo?.tradeName;

  const phoneNumberData = customerInfo?.phoneNumbers?.[0];
  const personPhoneNumber =
    phoneNumberData &&
    formatPhoneNumber(
      `${phoneNumberData?.prefix}${phoneNumberData?.number}`,
      phoneNumberData?.countryCode as CountryCode | Nullish
    );

  const isLoading = isEntityTagsLoading || isVehicleLoading || isCustomerLoading;
  const isError = isCustomerError || isVehicleError;

  return (
    <PortalManager zIndex={theme.zIndices.TOOLTIP}>
      <Portal>
        {/* eslint-disable-next-line react/forbid-dom-props -- Pre-existing violation */}
        <div ref={setFloating} style={{...floatingStyles, pointerEvents: 'none'}}>
          <DataStatus isLoading={isLoading} isError={isError}>
            <TestDriveTooltipCard
              vehicleTitle={vehicleData?.title}
              state={testDriveEvent.testDriveData.state}
              start={parseDate(testDriveEvent.testDriveData.scheduledAt)}
              end={parseDate(testDriveEvent.testDriveData.returnedAt)}
              customerName={customerName}
              phoneNumbers={isNotNil(personPhoneNumber) ? [personPhoneNumber] : []}
              isCompanyCustomer={isCompany}
              businessCaseCode={props.event.businessCaseCode}
              tags={entityTags?.map((tag) => (
                <Flag
                  key={tag.tagId}
                  size="small"
                  colorScheme={tag.valueColor}
                  label={tag.valueName}
                />
              ))}
              data-testid="vehicleTestDrivesCalendar"
            />
          </DataStatus>
        </div>
      </Portal>
    </PortalManager>
  );
}
